import { useState } from 'react'
import type { MapFeature, MissionTeam, Participant } from '../api/types'
import { type FeatureEdits, isDirty, kindName } from '../import/featureEdit'
import { Modal } from './Modal'

/** Colours an organiser can pick, the affiliation set first so meaning stays consistent. */
const SWATCHES: { hex: string | null; name: string }[] = [
  { hex: null, name: 'Default' },
  { hex: '#5B8FE0', name: 'Friendly blue' },
  { hex: '#E24A42', name: 'Hostile red' },
  { hex: '#4FA85E', name: 'Neutral green' },
  { hex: '#E5C94B', name: 'Unknown yellow' },
  { hex: '#E8913A', name: 'Orange' },
  { hex: '#B07CE0', name: 'Purple' },
  { hex: '#E8EFE9', name: 'White' },
]

const VISIBILITY: { value: MapFeature['visibility']; label: string; hint: string }[] = [
  { value: 'TEAM', label: 'Team', hint: 'The team that drew it' },
  { value: 'FACTION', label: 'Side', hint: 'Everybody on that side' },
  { value: 'MISSION', label: 'Everyone', hint: 'Both sides' },
]

/**
 * One feature, selected on the map: what it is, and -- for a host -- changing or removing it.
 *
 * Floats over the map's corner rather than replacing a column, so the shape being edited stays in
 * view while it is edited. Geometry is changed on the map itself (drag the marker or the corner
 * handles); this panel holds everything that is words and settings.
 */
export function FeatureEditor({
  feature,
  edits,
  editable,
  saving,
  teams,
  roster,
  onChange,
  onSave,
  onRevert,
  onDelete,
  onClose,
}: {
  feature: MapFeature
  edits: FeatureEdits
  editable: boolean
  saving: boolean
  teams: MissionTeam[]
  roster: Participant[]
  onChange: (edits: FeatureEdits) => void
  onSave: () => void
  onRevert: () => void
  onDelete: () => Promise<void>
  onClose: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const dirty = isDirty(feature, edits)
  const team = teams.find((entry) => entry.teamId === feature.teamId)
  const author = roster.find((entry) => entry.userId === feature.createdBy)
  const name = feature.label || kindName(feature)
  const isShape = feature.geometryType !== 'POINT'

  return (
    <div className="floating-panel" role="dialog" aria-label={`Edit ${name}`}>
      <div className="spread">
        <div>
          <div className="eyebrow">{kindName(feature)}</div>
          <h2 style={{ marginTop: 2 }}>{name}</h2>
        </div>
        <button className="icon-button" aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      <p className="tertiary" style={{ margin: '6px 0 0' }}>
        {[team?.teamName ?? (feature.teamId ? 'A team' : 'Organisers'), author?.callsign]
          .filter(Boolean)
          .join(' · ')}
        {measure(feature) && ` · ${measure(feature)}`}
      </p>

      {!editable ? (
        <>
          {feature.remarks && <p style={{ whiteSpace: 'pre-wrap' }}>{feature.remarks}</p>}
          <p className="tertiary">Only a host of the event can change this.</p>
        </>
      ) : (
        <div className="stack" style={{ marginTop: 14, gap: 12 }}>
          <div className="field">
            <label htmlFor="feature-label">Name</label>
            <input
              id="feature-label"
              value={edits.label}
              maxLength={64}
              placeholder={kindName(feature)}
              onChange={(event) => onChange({ ...edits, label: event.target.value })}
            />
          </div>

          <div className="field">
            <label htmlFor="feature-remarks">Briefing</label>
            <textarea
              id="feature-remarks"
              value={edits.remarks}
              maxLength={2000}
              rows={3}
              onChange={(event) => onChange({ ...edits, remarks: event.target.value })}
            />
          </div>

          <fieldset className="field">
            <legend>Who sees it</legend>
            <div className="segmented" role="radiogroup">
              {VISIBILITY.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={edits.visibility === option.value}
                  title={option.hint}
                  className={edits.visibility === option.value ? 'selected' : ''}
                  onClick={() => onChange({ ...edits, visibility: option.value })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="field">
            <legend>Colour</legend>
            <div className="swatches">
              {SWATCHES.map((swatch) => (
                <button
                  key={swatch.name}
                  type="button"
                  aria-label={swatch.name}
                  aria-pressed={edits.colorHex === swatch.hex}
                  title={swatch.name}
                  className={`swatch${edits.colorHex === swatch.hex ? ' selected' : ''}`}
                  style={{ background: swatch.hex ?? 'transparent' }}
                  onClick={() => onChange({ ...edits, colorHex: swatch.hex })}
                >
                  {swatch.hex === null && <span aria-hidden>A</span>}
                </button>
              ))}
            </div>
          </fieldset>

          {feature.geometryType === 'CIRCLE' && (
            <div className="field">
              <label htmlFor="feature-radius">Radius (metres)</label>
              <input
                id="feature-radius"
                type="number"
                min={1}
                value={Math.round(edits.radiusMetres ?? 0)}
                onChange={(event) =>
                  onChange({ ...edits, radiusMetres: Math.max(1, Number(event.target.value)) })
                }
              />
            </div>
          )}

          <label className="check">
            <input
              type="checkbox"
              checked={edits.objective}
              onChange={(event) => onChange({ ...edits, objective: event.target.checked })}
            />
            List it as an objective
          </label>

          <p className="tertiary" style={{ margin: 0 }}>
            {isShape
              ? 'Drag the white handles on the map to reshape it.'
              : 'Drag the marker on the map to move it.'}
          </p>

          <div className="row" style={{ justifyContent: 'space-between' }}>
            <button className="danger-outline" onClick={() => setConfirming(true)}>
              Delete
            </button>
            <div className="row">
              {dirty && (
                <button className="secondary" onClick={onRevert} disabled={saving}>
                  Undo changes
                </button>
              )}
              <button onClick={onSave} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirming && (
        <Modal title={`Delete ${name}?`} onClose={() => setConfirming(false)}>
          <div className="stack">
            <p style={{ margin: 0 }}>
              It disappears from every map in the event, players' phones included. This cannot be
              undone.
            </p>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="secondary" onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button
                className="danger"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true)
                  await onDelete()
                  setDeleting(false)
                  setConfirming(false)
                }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

function measure(feature: MapFeature): string | null {
  if (feature.geometryType === 'CIRCLE' && feature.radiusMetres) {
    return `${Math.round(feature.radiusMetres)} m radius`
  }
  if (feature.areaSquareMetres) {
    const area = feature.areaSquareMetres
    return area >= 10_000 ? `${(area / 10_000).toFixed(2)} ha` : `${Math.round(area)} m²`
  }
  if (feature.lengthMetres && feature.geometryType !== 'POINT') {
    const length = feature.lengthMetres
    return length >= 1000 ? `${(length / 1000).toFixed(2)} km` : `${Math.round(length)} m`
  }
  return null
}

export function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden fill="none">
      <path d="M3.5 3.5l9 9m0-9l-9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}
